import {PAYMENT_METHODS} from '@optimaxdev/analytics/desktop/constants/paymentMethods';
import {waitForFulfilled, WaitForFulfilledType} from '@optimaxdev/utils';
import {SagaIterator} from 'redux-saga';
import {call, select, take} from 'redux-saga/effects';

import {STEPS} from 'desktop/pages/checkout/constants/checkout';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {ApplicationStoreType} from 'desktop/store';

import {AddressType} from 'desktop/types/addressType';

import {CustomerAddressType} from 'desktop/types/customerAddressesType';

import {apiConfig} from 'libs/api/api';

import {isApplePayAvailable} from 'libs/checkout/checkout';

import {setAddressLoaded} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {getPaymentMethods as getPaymentMethodsAction} from '../../../reducers/paymentStep/paymentMethods/paymentMethods';
import {getAddresses} from '../../../selectors/deliveryStep/deliveryStep';
import {
    getPaymentMethods,
    getIsPaymentMethodFulfilled,
} from '../../../selectors/paymentStep/paymentStep';
import {getShippingAddress} from '../../../selectors/shippingAddress/shippingAddress';

/**
 * Get list of keys payment methods
 * @returns {string[]} keys of payment methods
 */
export function* getPaymentMethodsKeysList() {
    yield call<WaitForFulfilledType<ApplicationStoreType>>(
        waitForFulfilled,
        getIsPaymentMethodFulfilled,
        `${getPaymentMethodsAction.fulfilled}`,
    );
    const {list} = yield select(getPaymentMethods);
    return Object.keys(list).sort(
        (prev, current) =>
            Number(list[prev as PAYMENTS]?.order) - Number(list[current as PAYMENTS]?.order),
    );
}

const requiredFieldsForLogged = ['address', 'city', 'firstName', 'lastName', 'phone', 'postal'];
const requiredFieldsForNonLogged = [...requiredFieldsForLogged, 'email'];
const notCountedAddressFields = ['country', 'state', 'regionID'];

/**
 * Get Address Fullness
 * @returns {boolean[]} initial address on checkout page
 */
export function* getAddressFullness(isLoggedIn: boolean) {
    const shippingAddress: AddressType = yield select(getShippingAddress);
    const shippingAddressKeys = Object.keys(shippingAddress);
    const savedCustomerAddresses: Array<CustomerAddressType> = yield select(getAddresses);

    const requiredAddressFields = isLoggedIn ? requiredFieldsForLogged : requiredFieldsForNonLogged;
    const hasFullAddress =
        Boolean(shippingAddressKeys.length) &&
        !shippingAddressKeys.some(
            field => requiredAddressFields.includes(field) && !shippingAddress[field].value?.trim(),
        );
    const hasPartialAddress = shippingAddressKeys.some(
        field =>
            !notCountedAddressFields.includes(field) &&
            Boolean(shippingAddress[field].value?.trim()),
    );
    const usedSavedAddress = savedCustomerAddresses.find(
        address => shippingAddress?.addressID?.value === address.addressId,
    );

    return [hasFullAddress, hasPartialAddress, Boolean(usedSavedAddress)];
}

/**
 * Get auto applied address
 * @returns {string} state of initial address on checkout page
 */
function* getAutoAppliedAddress(): SagaIterator<string> {
    const isLoggedIn: boolean = apiConfig.hostStore.getState().customer.is_logged_in;
    if (isLoggedIn) yield take(`${setAddressLoaded}`);
    const [hasFullAddress, hasPartialAddress, usedSavedAddress] = yield call(
        getAddressFullness,
        isLoggedIn,
    );

    if (isLoggedIn && usedSavedAddress) return 'True - Logged In User - fully fulfilled';

    if (hasFullAddress) return 'True - Saved From Previous - fully fulfilled';
    if (hasPartialAddress) return 'True - Saved From Previous - partially fulfilled';

    return 'False';
}

/**
 * Returns additional fields for EEC Event whichever step
 * @param {string} step Checkout step
 * @returns {{} | {availablePaymentMethods: string}} Object with additional field
 */
export function* getAdditionalFields(step: string) {
    if (step === STEPS.PAYMENT_STEP) {
        const list: string[] = yield call(getPaymentMethodsKeysList);

        const availablePaymentMethods: string = Array.from(
            new Set(
                list.reduce((acc: string[], value: string) => {
                    if (
                        !isApplePayAvailable() &&
                        (value === PAYMENTS.METHOD_APPLE_PAY ||
                            value === PAYMENTS.METHOD_PRIMER_APPLE_PAY)
                    )
                        return acc;

                    if (value === PAYMENTS.METHOD_PAYPAL_V3) return [...acc, 'Paypal'];

                    return PAYMENT_METHODS[value] ? [...acc, PAYMENT_METHODS[value]] : acc;
                }, []),
            ),
        ).join(',');
        return {availablePaymentMethods};
    }
    if (step === STEPS.DELIVERY_STEP) {
        const autoAppliedAddress: string = yield call(getAutoAppliedAddress);
        return {autoAppliedAddress};
    }
    return {};
}
